import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ReceiptRefundIcon,
  ClockIcon,
  CheckCircleIcon,
  BanknotesIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { expensesService } from '../../../services/accounting/expenses';
import { expenseExportService } from '../../../services/reports/expense-export';
import LoadingSpinner from '../../ui/LoadingSpinner';
import toast from 'react-hot-toast';
import CreateExpenseModal from '../modals/CreateExpenseModal';
import EditExpenseModal from '../modals/EditExpenseModal';
import ViewExpenseModal from '../modals/ViewExpenseModal';
import ReceiptUploadModal from '../modals/ReceiptUploadModal';
import ReceiptPreviewModal from '../modals/ReceiptPreviewModal';
import CategoryManagementModal from '../modals/CategoryManagementModal';
import ExpenseBreakdownModal from '../modals/ExpenseBreakdownModal';

const ExpensesTab = () => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [approvedExpenses, setApprovedExpenses] = useState([]);
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);

  const loadExpenses = useCallback(async () => {
    if (!currentCompany?.id) return;
    setLoading(true);
    try {
      const expensesData = await expensesService.getExpenses(currentCompany.id);
      setExpenses(expensesData);
      setApprovedExpenses(expensesData.filter(expense => expense.status === 'approved'));
      setPendingExpenses(expensesData.filter(expense => expense.status === 'pending'));
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error(t('common.error_loading_data', 'Failed to load expenses'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  useEffect(() => {
    loadExpenses();
    loadCategories();
  }, [loadExpenses]);

  const loadCategories = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    try {
      // Load custom categories from localStorage
      const storedCategories = localStorage.getItem(`expense_categories_${currentCompany.id}`);
      const customCategories = storedCategories ? JSON.parse(storedCategories) : [];
      
      // Default categories
      const defaultCategories = [
        { id: 'office', name: t('expenses.office_expenses', 'Office Expenses'), icon: '🏢', color: 'blue', isDefault: true },
        { id: 'marketing', name: t('expenses.marketing_expenses', 'Marketing'), icon: '📢', color: 'purple', isDefault: true },
        { id: 'salary', name: t('expenses.salary_expenses', 'Salaries'), icon: '💰', color: 'green', isDefault: true },
        { id: 'utility', name: t('expenses.utility_expenses', 'Utilities'), icon: '⚡', color: 'yellow', isDefault: true },
        { id: 'equipment', name: t('expenses.equipment_expenses', 'Equipment'), icon: '🔧', color: 'gray', isDefault: true },
        { id: 'legal', name: t('expenses.legal_expenses', 'Legal'), icon: '⚖️', color: 'indigo', isDefault: true },
        { id: 'travel', name: t('expenses.travel_expenses', 'Travel'), icon: '✈️', color: 'sky', isDefault: true },
        { id: 'insurance', name: t('expenses.insurance_expenses', 'Insurance'), icon: '🛡️', color: 'emerald', isDefault: true },
        { id: 'maintenance', name: t('expenses.maintenance_expenses', 'Maintenance'), icon: '🔨', color: 'orange', isDefault: true },
        { id: 'fuel', name: t('expenses.fuel_expenses', 'Fuel Expenses'), icon: '⛽', color: 'red', isDefault: true },
        { id: 'truck_washing', name: t('expenses.truck_washing_expenses', 'Truck Washing'), icon: '🚿', color: 'cyan', isDefault: true },
        { id: 'oil_change', name: t('expenses.oil_change_expenses', 'Oil Changes'), icon: '🛢️', color: 'amber', isDefault: true },
        { id: 'commission', name: t('expenses.commission_expenses', 'Commission'), icon: '💼', color: 'teal', isDefault: true },
        { id: 'other', name: t('expenses.other_expenses', 'Other'), icon: '📋', color: 'slate', isDefault: true }
      ];
      
      // Combine default and custom categories
      const allCategories = [...defaultCategories, ...customCategories];
      setCategories(allCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error(t('expenses.error.loading_categories_failed', 'Failed to load categories'));
    }
  }, [currentCompany?.id, t]);

  useEffect(() => {
    if (currentCompany?.id) {
      loadExpenses();
      loadCategories();
    }
  }, [currentCompany?.id, loadExpenses, loadCategories]);

  const getCategoryInfo = (categoryId) => {
    return categories.find(cat => cat.id === categoryId) || categories.find(cat => cat.id === 'other') || {
      id: 'other',
      name: 'Other',
      icon: '📋',
      color: 'slate'
    };
  };

  const formatDateTime = (expense) => {
    // Handle different date formats and sources
    let expenseDate;
    let creationTime;

    // Try to parse expense date - prioritize actual expense date fields
    if (expense.date) {
      expenseDate = expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
    } else if (expense.expenseDate) {
      expenseDate = expense.expenseDate?.toDate ? expense.expenseDate.toDate() : new Date(expense.expenseDate);
    } else if (expense.createdAt) {
      expenseDate = expense.createdAt?.toDate ? expense.createdAt.toDate() : new Date(expense.createdAt);
    } else {
      expenseDate = new Date(); // Fallback to current date
    }

    // Validate expense date
    if (isNaN(expenseDate.getTime())) {
      expenseDate = new Date(); // Fallback to current date
    }

    // Try to parse creation time
    if (expense.createdAt?.toDate) {
      // Firestore timestamp
      creationTime = expense.createdAt.toDate();
    } else if (expense.createdAt) {
      creationTime = new Date(expense.createdAt);
    } else if (expense.submittedAt) {
      creationTime = new Date(expense.submittedAt);
    } else {
      creationTime = expenseDate;
    }

    // Validate creation time
    if (isNaN(creationTime.getTime())) {
      creationTime = expenseDate;
    }

    const dateFormatted = expenseDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
    const timeFormatted = creationTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return { dateFormatted, timeFormatted, creationTime, expenseDate };
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'badge badge-warning', text: t('accounting.pending_approval', 'Pending Approval') },
      approved: { class: 'badge badge-success', text: t('accounting.approved', 'Approved') },
      rejected: { class: 'badge badge-error', text: t('quotations.rejected') },
      paid: { class: 'badge badge-primary', text: t('common.paid') }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={config.class}>{config.text}</span>;
  };

  // Fix mixed operators with parentheses
  // Sort expenses by newest first, then filter
  const sortedExpenses = [...expenses].sort((a, b) => {
    // Primary sort: by expense date (newest first)
    const dateA = new Date(a.date || a.expenseDate || a.createdAt?.toDate?.() || 0);
    const dateB = new Date(b.date || b.expenseDate || b.createdAt?.toDate?.() || 0);
    const dateDiff = dateB - dateA;
    
    // Secondary sort: by creation time if dates are the same
    if (dateDiff === 0) {
      const createdA = new Date(a.createdAt?.toDate?.() || a.submittedAt || 0);
      const createdB = new Date(b.createdAt?.toDate?.() || b.submittedAt || 0);
      return createdB - createdA;
    }
    
    return dateDiff;
  });

  const filteredExpenses = sortedExpenses.filter(expense => {
    const matchesSearch = expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expense.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || expense.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || expense.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
  const thisMonthExpenses = expenses.filter(exp => {
    // Get the expense date using the same logic as formatDateTime
    let expDate;
    if (exp.date) {
      expDate = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
    } else if (exp.expenseDate) {
      expDate = exp.expenseDate?.toDate ? exp.expenseDate.toDate() : new Date(exp.expenseDate);
    } else if (exp.createdAt) {
      expDate = exp.createdAt?.toDate ? exp.createdAt.toDate() : new Date(exp.createdAt);
    } else {
      return false; // Skip if no valid date
    }
    
    // Validate date
    if (isNaN(expDate.getTime())) {
      return false;
    }
    
    const now = new Date();
    return expDate.getMonth() === now.getMonth() && expDate.getFullYear() === now.getFullYear();
  }).reduce((sum, exp) => sum + (exp.totalAmount || exp.amount || 0), 0);

  const categoryBreakdown = categories.map(category => {
    const categoryExpenses = expenses.filter(exp => exp.category === category.id);
    const categoryTotal = categoryExpenses.reduce((sum, exp) => sum + (exp.totalAmount || exp.amount || 0), 0);
    return {
      ...category,
      value: categoryTotal,
      count: categoryExpenses.length
    };
  });

  const handleViewExpense = (expense) => {
    setSelectedExpense(expense);
    setShowViewModal(true);
  };
  
  const handleViewBreakdown = (expense) => {
    setSelectedExpense(expense);
    setShowBreakdownModal(true);
  };
  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setShowEditModal(true);
  };
  const handleUploadReceipt = (expense) => {
    setSelectedExpense(expense);
    setShowReceiptModal(true);
  };
  const handlePreviewReceipt = (receiptUrl) => {
    setPreviewReceiptUrl(receiptUrl);
    setShowReceiptPreview(true);
  };
  const handleApproveExpense = async (expenseId) => {
    try {
      await expensesService.updateExpense(expenseId, { status: 'approved' });
      toast.success(t('accounting.expense_approved'));
      loadExpenses();
    } catch (error) {
      toast.error(t('expenses.error.approval_failed'));
    }
  };
  const handleRejectExpense = async (expenseId) => {
    try {
      await expensesService.updateExpense(expenseId, { status: 'rejected' });
      toast.success(t('accounting.expense_rejected'));
      loadExpenses();
    } catch (error) {
      toast.error(t('expenses.error.rejection_failed'));
    }
  };
  const handleDeleteExpense = async (expenseId) => {
    if (window.confirm(t('expenses.confirm_delete', 'Are you sure you want to delete this expense?'))) {
      try {
        await expensesService.deleteExpense(expenseId);
        toast.success(t('expenses.success.expense_deleted'));
        loadExpenses();
      } catch (error) {
        toast.error(t('clients.delete_failed'));
      }
    }
  };

  const handleExportReport = async (format = 'csv') => {
    try {
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
      
      let blob;
      let fileName;
      
      if (format === 'excel') {
        // Export as Excel with enhanced formatting
        blob = await expenseExportService.generateExpenseReport(currentCompany.id, {
          startDate: null, // Could be extended to support date filters
          endDate: null,
          categoryFilter: categoryFilter,
          statusFilter: statusFilter,
          language: currentLanguage
        });
        fileName = `expenses_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      } else {
        // Export as CSV with VAT breakdown
        blob = expenseExportService.generateExpenseCSV(filteredExpenses, currentLanguage);
        fileName = `expenses_report_${new Date().toISOString().split('T')[0]}.csv`;
      }

      // Create and download file
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(t('expenses.export_success', 'Expenses report exported successfully'));
    } catch (error) {
      console.error('Error exporting expenses:', error);
      toast.error(t('expenses.export_error', 'Failed to export expenses report'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body text-center py-4">
            <ReceiptRefundIcon className="w-6 h-6 mx-auto text-blue-600 mb-2" />
            <div className="text-xl font-bold text-gray-900">SAR {totalExpenses.toLocaleString()}</div>
            <div className="text-xs text-gray-500">{t('expenses.total_expenses', 'Total Expenses')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center py-4">
            <ClockIcon className="w-6 h-6 mx-auto text-yellow-600 mb-2" />
            <div className="text-xl font-bold text-yellow-600">SAR {pendingExpenses.reduce((sum, exp) => sum + (exp.totalAmount || exp.amount || 0), 0).toLocaleString()}</div>
            <div className="text-xs text-gray-500">{t('expenses.pending_approval', 'Pending Approval')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center py-4">
            <CheckCircleIcon className="w-6 h-6 mx-auto text-green-600 mb-2" />
            <div className="text-xl font-bold text-green-600">SAR {approvedExpenses.reduce((sum, exp) => sum + (exp.totalAmount || exp.amount || 0), 0).toLocaleString()}</div>
            <div className="text-xs text-gray-500">{t('expenses.approved_expenses', 'Approved Expenses')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center py-4">
            <BanknotesIcon className="w-6 h-6 mx-auto text-purple-600 mb-2" />
            <div className="text-xl font-bold text-purple-600">SAR {thisMonthExpenses.toLocaleString()}</div>
            <div className="text-xs text-gray-500">{t('expenses.this_month', t('common.this_month'))}</div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">{t('expenses.expense_breakdown', 'Expense Breakdown by Category')}</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
            {categoryBreakdown.map((category) => (
              <div key={category.id} className="text-center p-2 bg-gray-50 rounded-lg">
                <div className="text-lg mb-1">{category.icon}</div>
                <div className="font-medium text-xs truncate">{category.name}</div>
                <div className="text-sm font-bold text-gray-900">SAR {category.value.toLocaleString()}</div>
                <div className="text-xs text-gray-500">{category.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('expenses.manage_expenses', 'Manage Expenses')}</h2>
          <p className="text-sm text-gray-500">{t('expenses.track_business_description', 'Track and categorize business expenses')}</p>
        </div>
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <div className="dropdown dropdown-end">
            <label tabIndex={0} className="btn btn-outline">
              📊 {t('expenses.export_report', 'Export Report')}
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </label>
            <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52">
              <li>
                <button onClick={() => handleExportReport('csv')} className="text-left">
                  📄 {t('expenses.export_csv', 'Export as CSV')}
                </button>
              </li>
              <li>
                <button onClick={() => handleExportReport('excel')} className="text-left">
                  📊 {t('expenses.export_excel', 'Export as Excel')}
                </button>
              </li>
            </ul>
          </div>
          <button onClick={() => setShowCategoryModal(true)} className="btn btn-outline">
            ⚙️ {t('expenses.manage_categories', 'Manage Categories')}
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
            <PlusIcon className="w-4 h-4 mr-2" />
            {t('expenses.add_expense', 'Add Expense')}
          </button>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔍
                </div>
                <input
                  type="text"
                  placeholder={t('expenses.search_placeholder', 'Search expenses...')}
                  className="input input-bordered w-full pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex space-x-4">
              <select
                className="select select-bordered"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">{t('expenses.filter.all_categories', 'All Categories')}</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
              <select
                className="select select-bordered"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('expenses.filter.all_statuses', 'All Statuses')}</option>
                <option value="pending">{t('expenses.status.pending', t('common.pending'))}</option>
                <option value="approved">{t('expenses.status.approved', 'Approved')}</option>
                <option value="rejected">{t('expenses.status.rejected', 'Rejected')}</option>
                <option value="paid">{t('expenses.status.paid', t('common.paid'))}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th className="w-32">{t('expenses.expense_date_created', 'Date & Created')}</th>
                    <th className="w-32">{t('expenses.table.category', 'Category')}</th>
                    <th className="w-24">{t('expenses.table.vendor', 'Vendor')}</th>
                    <th className="w-80">{t('expenses.table.description', t('common.description'))}</th>
                    <th className="w-32">{t('expenses.table.amount', t('common.amount'))}</th>
                    <th className="w-24">{t('expenses.table.receipt', 'Receipt')}</th>
                    <th className="w-24">{t('expenses.table.actions', t('common.actions'))}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((expense, index) => {
                    const categoryInfo = getCategoryInfo(expense.category);
                    const { dateFormatted, timeFormatted, creationTime, expenseDate } = formatDateTime(expense);

                    // Extract shipment number from description and make descriptions shorter
                    const isCommissionExpense = expense.category === 'commission' || expense.description?.toLowerCase().includes('commission');
                    const isMaintenanceExpense = expense.category === 'maintenance' || expense.description?.toLowerCase().includes('maintenance');
                    const isFuelExpense = expense.category === 'fuel' || expense.description?.toLowerCase().includes('fuel');

                    let shortDescription = expense.description;
                    let shipmentNumber = null;
                    let routeInfo = null;

                    if (isCommissionExpense && expense.description) {
                      // Handle different commission description formats
                      let employeeName = null;

                      // Format 1: "Commission payment for [name] - Shipment [number]"
                      let match = expense.description.match(/Commission payment for (.+?) - Shipment (.+)/);
                      if (match) {
                        employeeName = match[1];
                        shipmentNumber = match[2];
                      } else {
                        // Format 2: "Driver commission for shipment [number] ([route])"
                        match = expense.description.match(/Driver commission for shipment (.+?) \((.+?)\)/);
                        if (match) {
                          shipmentNumber = match[1];
                          routeInfo = match[2];
                          employeeName = expense.vendor || 'Driver';
                        } else {
                          // Format 3: "Commission payment for driver - Shipment [number]"
                          match = expense.description.match(/Commission payment for driver - Shipment (.+)/);
                          if (match) {
                            shipmentNumber = match[1];
                            employeeName = expense.vendor || 'Driver';
                          }
                        }
                      }

                      if (employeeName) {
                        shortDescription = `Commission - ${employeeName}`;
                      }

                      // Try to get route info from expense notes if not already found
                      if (!routeInfo && expense.notes && expense.notes.includes('→')) {
                        routeInfo = expense.notes.trim();
                      }
                    } else if (isMaintenanceExpense && expense.description?.includes('Maintenance expense for shipment')) {
                      // Shorten maintenance descriptions
                      const match = expense.description.match(/Maintenance expense for shipment (.+)/);
                      if (match) {
                        shipmentNumber = match[1];
                        shortDescription = 'Maintenance';
                      }
                    } else if (isFuelExpense && expense.description?.includes('Fuel expense for shipment')) {
                      // Shorten fuel descriptions
                      const match = expense.description.match(/Fuel expense for shipment (.+)/);
                      if (match) {
                        shipmentNumber = match[1];
                        shortDescription = 'Fuel';
                      }
                    } else {
                      // Make other descriptions more compact
                      if (shortDescription && shortDescription.length > 30) {
                        shortDescription = shortDescription.substring(0, 27) + '...';
                      }
                    }

                    return (
                      <tr key={expense.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td>
                          <div className="font-medium">{dateFormatted}</div>
                          <div className="text-xs text-gray-500">
                            {timeFormatted}
                          </div>
                          <div className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 inline-block">
                            EXP{expense.id?.replace(/[^0-9]/g, '').slice(-6).padStart(6, '0') || expense.docId?.replace(/[^0-9]/g, '').slice(-6).padStart(6, '0') || '000001'}
                          </div>
                        </td>
                        <td className="relative">
                          <div className="flex items-center space-x-2 pr-8">
                            <span className="text-lg">{categoryInfo.icon}</span>
                            <span className="font-medium">{categoryInfo.name}</span>
                          </div>
                          <div className="absolute top-1 right-1 text-[8px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded leading-none">
                            {((expense.createdBy === 'system') ||
                              expense.relatedShipmentId ||
                              expense.tags?.includes('commission') ||
                              expense.tags?.includes('payroll') ||
                              (expense.category === 'commission') ||
                              ((expense.category === 'salary') && expense.description?.includes('Commission payment')) ||
                              expense.description?.includes('Driver commission') ||
                              expense.description?.includes('Maintenance expense') ||
                              expense.description?.includes('Fuel expense')) ?
                              'تلقائي' :
                              'يدوي'
                            }
                          </div>
                        </td>
                        <td>
                          <div className="font-medium text-sm">{expense.vendor || 'N/A'}</div>
                          {expense.driverName && (
                            <div className="text-xs text-gray-500">Driver: {expense.driverName}</div>
                          )}
                          {expense.truckNumber && (
                            <div className="text-xs text-gray-500">Truck: {expense.truckNumber}</div>
                          )}
                          {expense.paymentMethod && (
                            <div className="text-xs text-gray-400">{expense.paymentMethod}</div>
                          )}
                        </td>
                        <td>
                          <div className="font-medium">{shortDescription}</div>
                          {shipmentNumber && (
                            <div className="mt-0.5 flex items-center space-x-2">
                              <a
                                href={`/shipments/${expense.relatedShipmentId || ''}`}
                                className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                onClick={(e) => {
                                  if (expense.relatedShipmentId) {
                                    e.preventDefault();
                                    window.location.href = `/shipments/${expense.relatedShipmentId}`;
                                  }
                                }}
                              >
                                {shipmentNumber}
                              </a>
                              {routeInfo && (
                                <span className="text-xs text-gray-500">({routeInfo})</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          {/* Enhanced VAT Breakdown Display */}
                          <div className="space-y-1">
                            {/* Base Amount */}
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-gray-600">{t('accounting.base_amount', 'Base Amount')}:</span>
                              <span className="font-medium">SAR {(expense.baseAmount || expense.amount || 0).toLocaleString()}</span>
                            </div>
                            
                            {/* VAT Amount */}
                            {(expense.vatAmount || 0) > 0 && (
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-600">{t('accounting.vat_amount', 'VAT Amount')}:</span>
                                <span className="font-medium text-orange-600">SAR {expense.vatAmount.toLocaleString()}</span>
                              </div>
                            )}
                            
                            {/* Total Amount */}
                            <div className="flex justify-between items-center text-sm font-semibold border-t pt-1">
                              <span className="text-gray-800">{t('accounting.total_amount', 'Total Amount')}:</span>
                              <span className="text-blue-600">SAR {(expense.totalAmount || expense.amount || 0).toLocaleString()}</span>
                            </div>
                            
                            {/* VAT Inclusion Indicator */}
                            {expense.vatIncluded !== undefined && (
                              <div className="text-xs text-gray-500 italic">
                                {expense.vatIncluded 
                                  ? t('accounting.vat_inclusive', 'VAT Inclusive') 
                                  : t('accounting.vat_exclusive', 'VAT Exclusive')
                                }
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center space-x-1">
                            {expense.receiptAttached ? (
                              <div className="flex items-center space-x-1">
                                <span className="text-green-600 text-lg">📄</span>
                                <span className="text-green-600 text-sm font-medium">Attached</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                <span className="text-red-600 text-sm font-medium">Missing</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center space-x-1">
                            <button
                              className="btn btn-xs btn-ghost"
                              title={t('common.view')}
                              onClick={() => handleViewExpense(expense)}
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              className="btn btn-xs btn-ghost text-blue-600"
                              title={t('accounting.view_vat_breakdown', 'View VAT Breakdown')}
                              onClick={() => handleViewBreakdown(expense)}
                            >
                              📊
                            </button>
                            <button
                              className="btn btn-xs btn-ghost"
                              title={t('common.edit')}
                              onClick={() => handleEditExpense(expense)}
                            >
                              ✏️
                            </button>
                            {expense.receiptAttached && expense.receiptUrl ? (
                              <button
                                className="btn btn-xs btn-ghost text-blue-600"
                                title="Quick View Receipt"
                                onClick={() => handlePreviewReceipt(expense.receiptUrl)}
                              >
                                👁️
                              </button>
                            ) : (
                              <button
                                className="btn btn-xs btn-ghost"
                                title="Upload Receipt"
                                onClick={() => handleUploadReceipt(expense)}
                              >
                                📎
                              </button>
                            )}
                            <button
                              className="btn btn-xs btn-ghost text-red-600"
                              title={t('common.delete')}
                              onClick={() => handleDeleteExpense(expense.id)}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredExpenses.length === 0 && (
                <div className="text-center py-12">
                  <ReceiptRefundIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{t('expenses.no_expenses', 'No expenses found')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showCreateModal && (
        <CreateExpenseModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadExpenses();
          }}
        />
      )}
      {showEditModal && selectedExpense && (
        <EditExpenseModal
          expense={selectedExpense}
          onClose={() => {
            setShowEditModal(false);
            setSelectedExpense(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedExpense(null);
            loadExpenses();
          }}
        />
      )}
      {showViewModal && selectedExpense && (
        <ViewExpenseModal
          expense={selectedExpense}
          onClose={() => {
            setShowViewModal(false);
            setSelectedExpense(null);
          }}
          onEdit={() => {
            setShowViewModal(false);
            setShowEditModal(true);
          }}
        />
      )}
      {showReceiptModal && selectedExpense && (
        <ReceiptUploadModal
          expense={selectedExpense}
          onClose={() => {
            setShowReceiptModal(false);
            setSelectedExpense(null);
          }}
          onSuccess={() => {
            setShowReceiptModal(false);
            setSelectedExpense(null);
            loadExpenses();
          }}
        />
      )}
      {showReceiptPreview && previewReceiptUrl && (
        <ReceiptPreviewModal
          receiptUrl={previewReceiptUrl}
          onClose={() => {
            setShowReceiptPreview(false);
            setPreviewReceiptUrl(null);
          }}
        />
      )}
      {showCategoryModal && (
        <CategoryManagementModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          onSuccess={() => {
            loadCategories();
            setShowCategoryModal(false);
          }}
        />
      )}
      {showBreakdownModal && selectedExpense && (
        <ExpenseBreakdownModal
          expense={selectedExpense}
          isOpen={showBreakdownModal}
          onClose={() => {
            setShowBreakdownModal(false);
            setSelectedExpense(null);
          }}
        />
      )}
    </div>
  );
};

export default ExpensesTab;
